import "./BoardList.css";
import { Alert } from "@ui/Alert";
import { Loader } from "@ui/Loader";
import { NoMatchingResults } from "@ui/molecules/NoMatchingResults/NoMatchingResults";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import {
	type Accessor,
	createEffect,
	createSignal,
	For,
	Match,
	type Resource,
	Switch,
} from "solid-js";
import type { ListBoardsResponse } from "@/client";
import { MoveBoardItem } from "./MoveBoardItem";

type Props = {
	boards?: Resource<ListBoardsResponse>;
	selectedBoardId: Accessor<string | null>;
	onSelectBoard: (boardId: string) => void;
};

export const BoardList = (props: Props) => {
	const [focusedIndex, setFocusedIndex] = createSignal<number>(-1);
	const [isListFocused, setIsListFocused] = createSignal(false);
	let listRef: HTMLDivElement | undefined;
	const itemRefs = new Map<number, HTMLButtonElement>();

	createEffect(() => {
		if (props.boards()) {
			setFocusedIndex(-1);
			itemRefs.clear();
		}
	});

	createEffect(() => {
		const index = focusedIndex();
		if (index >= 0 && isListFocused()) {
			requestAnimationFrame(() => {
				const item = itemRefs.get(index);
				if (item) {
					item.focus();
				}
			});
		}
	});

	const getColumnCount = (): number => {
		if (!listRef) return 1;
		const computedStyle = window.getComputedStyle(listRef);
		const gridTemplateColumns = computedStyle.gridTemplateColumns;
		if (gridTemplateColumns && gridTemplateColumns !== "none") {
			const tracks = gridTemplateColumns
				.split(" ")
				.filter((t) => t.trim() !== "");
			if (tracks.length > 0) {
				return tracks.length;
			}
		}
		const containerWidth = listRef.clientWidth;
		return Math.max(1, Math.floor(containerWidth / 200));
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		const boardsList = props.boards()?.boards;
		if (!boardsList || boardsList.length === 0) return;
		const currentIndex = focusedIndex();
		const totalItems = boardsList.length;

		switch (e.key) {
			case "ArrowDown": {
				e.preventDefault();
				e.stopPropagation();
				if (!isListFocused()) {
					setIsListFocused(true);
				}
				const nextIndex =
					currentIndex < 0
						? 0
						: currentIndex < totalItems - 1
							? currentIndex + 1
							: 0;
				setFocusedIndex(nextIndex);
				requestAnimationFrame(() => {
					const item = itemRefs.get(nextIndex);
					if (item) {
						item.focus();
					}
				});
				break;
			}
			case "ArrowUp": {
				e.preventDefault();
				e.stopPropagation();
				if (!isListFocused()) {
					setIsListFocused(true);
				}
				const prevIndex =
					currentIndex < 0
						? totalItems - 1
						: currentIndex > 0
							? currentIndex - 1
							: totalItems - 1;
				setFocusedIndex(prevIndex);
				requestAnimationFrame(() => {
					const item = itemRefs.get(prevIndex);
					if (item) {
						item.focus();
					}
				});
				break;
			}
			case "ArrowRight": {
				e.preventDefault();
				e.stopPropagation();
				if (!isListFocused()) {
					setIsListFocused(true);
				}
				const columnCount = getColumnCount();
				let nextIndex: number;
				if (currentIndex < 0) {
					nextIndex = 0;
				} else {
					const calculatedIndex = currentIndex + columnCount;
					if (calculatedIndex < totalItems) {
						nextIndex = calculatedIndex;
					} else {
						const currentRow = Math.floor(currentIndex / columnCount);
						nextIndex = currentRow * columnCount;
					}
				}
				setFocusedIndex(nextIndex);
				requestAnimationFrame(() => {
					const item = itemRefs.get(nextIndex);
					if (item) {
						item.focus();
					}
				});
				break;
			}
			case "ArrowLeft": {
				e.preventDefault();
				e.stopPropagation();
				if (!isListFocused()) {
					setIsListFocused(true);
				}
				const columnCount = getColumnCount();
				let prevIndex: number;
				if (currentIndex < 0) {
					prevIndex = totalItems - 1;
				} else {
					const calculatedIndex = currentIndex - columnCount;
					if (calculatedIndex >= 0) {
						prevIndex = calculatedIndex;
					} else {
						const currentRow = Math.floor(currentIndex / columnCount);
						prevIndex = Math.min(
							(currentRow + 1) * columnCount - 1,
							totalItems - 1,
						);
					}
				}
				setFocusedIndex(prevIndex);
				requestAnimationFrame(() => {
					const item = itemRefs.get(prevIndex);
					if (item) {
						item.focus();
					}
				});
				break;
			}
			case "Tab":
				if (isListFocused()) {
					setIsListFocused(false);
					setFocusedIndex(-1);
				}
				break;
			case "Enter":
			case " ":
				if (isListFocused() && currentIndex >= 0) {
					e.preventDefault();
					e.stopPropagation();
					const board = boardsList[currentIndex];
					if (board?.id) {
						props.onSelectBoard(board.id);
					}
				}
				break;
			case "Home":
				if (isListFocused()) {
					e.preventDefault();
					e.stopPropagation();
					setFocusedIndex(0);
					requestAnimationFrame(() => {
						const item = itemRefs.get(0);
						if (item) {
							item.focus();
						}
					});
				}
				break;
			case "End":
				if (isListFocused()) {
					e.preventDefault();
					e.stopPropagation();
					const lastIndex = totalItems - 1;
					setFocusedIndex(lastIndex);
					requestAnimationFrame(() => {
						const item = itemRefs.get(lastIndex);
						if (item) {
							item.focus();
						}
					});
				}
				break;
		}
	};

	const handleListFocus = (e: FocusEvent) => {
		if (e.target === listRef) {
			setIsListFocused(true);
			const boardsList = props.boards()?.boards;
			if (boardsList && boardsList.length > 0) {
				setFocusedIndex(0);
				requestAnimationFrame(() => {
					const firstItem = itemRefs.get(0);
					if (firstItem) {
						firstItem.focus();
					}
				});
			}
		}
	};

	const handleListBlur = (e: FocusEvent) => {
		const relatedTarget = e.relatedTarget as Node | null;
		if (relatedTarget && listRef?.contains(relatedTarget)) {
			return;
		}
		setIsListFocused(false);
		setFocusedIndex(-1);
	};

	return (
		<VStack spacing="medium">
			<Text
				as="h3"
				family="mono"
				size="small"
				weight="bold"
				transform="uppercase"
			>
				Select board
			</Text>
			<Switch>
				<Match when={props.boards.loading}>
					<Loader />
				</Match>
				<Match when={props.boards() && props.boards().boards.length === 0}>
					<NoMatchingResults
						message="No boards found"
						hint="Create a board to get started"
					/>
				</Match>
				<Match when={props.boards()}>
					<div
						ref={listRef}
						class="board-list"
						role="listbox"
						aria-multiselectable="true"
						tabIndex={0}
						aria-label="Board list"
						onKeyDown={handleKeyDown}
						onFocusIn={handleListFocus}
						onFocusOut={handleListBlur}
					>
						<For each={props.boards().boards}>
							{(board, index) => {
								const idx = index();
								return (
									<MoveBoardItem
										ref={(el) => {
											if (el) {
												itemRefs.set(idx, el);
											} else {
												itemRefs.delete(idx);
											}
										}}
										onClick={() => props.onSelectBoard(board.id)}
										onFocus={() => {
											setIsListFocused(true);
											setFocusedIndex(idx);
										}}
										board={board}
										isSelected={() => props.selectedBoardId() === board.id}
										isFocused={() => focusedIndex() === idx}
										index={idx}
									/>
								);
							}}
						</For>
					</div>
				</Match>
				<Match when={props.boards.error}>
					<Alert variant="error">Error: {props.boards.error.message}</Alert>
				</Match>
			</Switch>
		</VStack>
	);
};
